import React from 'react';
import { Link } from 'react-router-dom';
import { Paper, Button } from '@c2fo/liquidity';
import { formatDateWithTimezone } from '../../../utils/dateFormat';
import { StatusBadge } from './StatusBadge';
import {
  DataTable,
  TableHeader,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableSubHeaderCell,
  TableCell,
} from './DataTable';
import type { OfferActivity } from '../types';

interface OfferActivityTableProps {
  offerActivity: OfferActivity[];
}

export const OfferActivityTable: React.FC<OfferActivityTableProps> = ({ offerActivity }) => {
  return (
    <Paper elevation={1} square className="p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold m-0">Offer Activity</h3>
        <Button variant="tertiary" size="sm" disabled>
          Limit: 10
        </Button>
      </div>
      {offerActivity.length > 0 ? (
        <DataTable aria-label="Offer activity table">
          <TableHeader>
            <tr>
              <TableHeaderCell className="w-10">#</TableHeaderCell>
              <TableHeaderCell className="min-w-[150px] text-left">Supplier</TableHeaderCell>
              <TableHeaderCell className="min-w-[100px]">Market</TableHeaderCell>
              <TableHeaderCell colSpan={2}>Updated Offer</TableHeaderCell>
              <TableHeaderCell colSpan={3}>Historic AP</TableHeaderCell>
              <TableHeaderCell colSpan={4}>Last Updated</TableHeaderCell>
              <TableHeaderCell className="text-left">SRM</TableHeaderCell>
            </tr>
            <tr>
              <TableSubHeaderCell>{''}</TableSubHeaderCell>
              <TableSubHeaderCell>{''}</TableSubHeaderCell>
              <TableSubHeaderCell>{''}</TableSubHeaderCell>
              <TableSubHeaderCell>Type</TableSubHeaderCell>
              <TableSubHeaderCell>Offer</TableSubHeaderCell>
              <TableSubHeaderCell>Change</TableSubHeaderCell>
              <TableSubHeaderCell>Eligible</TableSubHeaderCell>
              <TableSubHeaderCell>Clearing</TableSubHeaderCell>
              <TableSubHeaderCell>Not Clearing</TableSubHeaderCell>
              <TableSubHeaderCell>Last Update By</TableSubHeaderCell>
              <TableSubHeaderCell>Time</TableSubHeaderCell>
              <TableSubHeaderCell>Expires</TableSubHeaderCell>
              <TableSubHeaderCell>Name</TableSubHeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {offerActivity.map((activity, index) => (
              <TableRow key={activity.id} index={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  {activity.makerOrganizationUuid && activity.divisionId ? (
                    <Link
                      to={`/divisions/${activity.makerOrganizationUuid}/${activity.divisionId}`}
                      className="text-blue-600 no-underline hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                      aria-label={`View division ${activity.takerName}`}
                    >
                      {activity.takerName}
                    </Link>
                  ) : (
                    <span>{activity.takerName}</span>
                  )}
                </TableCell>
                <TableCell>
                  {activity.currency} {activity.makerName}
                </TableCell>
                <TableCell>
                  <StatusBadge
                    type={activity.parsed?.offerType === 'DISC' ? 'error' : 'info'}
                    label={activity.parsed?.offerType || '-'}
                    aria-label={`Offer type: ${activity.parsed?.offerType || 'Unknown'}`}
                  />
                </TableCell>
                <TableCell>
                  {activity.parsed?.offerType === 'DISC' ? `${activity.maxDiscount || 0}%` : `${activity.maxApr || 0}%`}
                </TableCell>
                <TableCell>
                  {activity.parsed?.offerDiff ? (
                    <span className="flex items-center gap-1">
                      {activity.parsed.offerDiff.startsWith('-') ? '↓' : '↑'}
                      Changed from {activity.parsed.old}% {activity.parsed.oldOfferType} to {activity.parsed.new}%{' '}
                      {activity.parsed.offerType}
                    </span>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>
                  {activity.lastUpdatedUserAuthServiceUuid ? (
                    <Link
                      to={`/users/${activity.lastUpdatedUserAuthServiceUuid}`}
                      className="text-blue-600 no-underline hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                      aria-label={`View user ${activity.lastUpdatedUserEmail}`}
                    >
                      {activity.lastUpdatedUserEmail}
                    </Link>
                  ) : (
                    <span>{activity.lastUpdatedUserEmail}</span>
                  )}
                </TableCell>
                <TableCell>{formatDateWithTimezone(activity.created)}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>
                  {activity.srmFirstName} {activity.srmLastName}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
      ) : (
        <div className="p-8 text-center text-gray-600" role="status" aria-live="polite">
          No offer activity found
        </div>
      )}
    </Paper>
  );
};
